import { ProductList } from "@packages/product-list/ProductList";
import { StandardProps } from "@/types/types";
import { transformProduct } from "@/utils/transform";
import { useExperienceString, useFeatureFlags } from "@/flags/flags";
import { useRouteParams } from "@/hooks/useRouteParams";
import Head from "next/head";
import { generateSrcSet } from "@packages/product-list/generateSrcSet";
import { applyFeatureFlagOverrides } from "@packages/feature-flags";

export function ProductGrid({
  productListingData,
  productListProps,
}: Pick<StandardProps, "productListingData" | "productListProps">) {
  const flags = useFeatureFlags();
  const route = useRouteParams();
  const experiencesRawString = useExperienceString();
  const numericPageSize = Number(flags.pageSize?.value);
  const numericCurrentPage = Number(route.query.page || 1);

  const products = productListingData.products.map((product) =>
    transformProduct(product),
  );

  const statefulProductListFlags = applyFeatureFlagOverrides({
    baseFlags: productListProps.flags,
    parameterFlags: {
      largeCards: flags.isLargeCardSizeFlagOn?.value,
      fullWidth: true,
    },
  });

  const { src, srcSet } = generateSrcSet(
    products[0]?.variants[0]?.images.primary || "",
  );

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          fetchPriority="high"
          href={src}
          imageSrcSet={srcSet}
        />
      </Head>
      <div
        id="main-content"
        data-testid="component-grid-container"
        aria-label="Product listing page results"
        data-list-config={productListProps.config.name}
      >
        <meta
          name="total-number-of-items"
          content={products.length.toString()}
        />
        <ProductList
          products={products}
          resultsInfo={{
            start: (numericCurrentPage - 1) * numericPageSize,
            end: numericCurrentPage * numericPageSize,
            pageSize: numericPageSize,
            total: productListingData.results,
          }}
          flags={statefulProductListFlags}
          config={productListProps.config}
          experiencesRawString={experiencesRawString}
          consumerId="web-plp-scaffold-ui"
        />
      </div>
    </>
  );
}
